import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    PeopleAlt,
    Chat,
    HelpOutline,
    EmojiPeople,
    CheckCircleOutline,
    ReportProblem,
    Lightbulb,
    ArrowForwardIos
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアでよくある人間関係の悩み
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('distance')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    上手な距離感の保ち方
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('communication')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    話し合いのコツとコミュニケーション術
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('advice')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    専門家のアドバイスと実践例
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('summary')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    まとめとQ&A
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入部分 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアでよくある人間関係の悩み
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、生活コストを抑えたり、新しいコミュニティでの出会いを楽しんだりできる魅力的な住まい方です。しかし「人間関係」に悩む声も少なくありません。<b>些細なすれ違いがストレスになったり、距離感がつかめずギクシャクしたり…</b>。実は、こうしたトラブルは特別なことではなく、多くの人が経験しています。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <PeopleAlt sx={{ fontSize: 38, color: '#1976d2', mb: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                ルームシェアでよくある「人間関係の悩み」
                            </Typography>
                            <List dense sx={{ mt: 1 }}>
                                <ListItem><ListItemIcon><ReportProblem color="warning" /></ListItemIcon><ListItemText primary="生活リズムの違い" /></ListItem>
                                <ListItem><ListItemIcon><ReportProblem color="warning" /></ListItemIcon><ListItemText primary="家事分担・掃除の不満" /></ListItem>
                                <ListItem><ListItemIcon><ReportProblem color="warning" /></ListItemIcon><ListItemText primary="プライバシー・距離感の問題" /></ListItem>
                                <ListItem><ListItemIcon><ReportProblem color="warning" /></ListItemIcon><ListItemText primary="お金の管理や支払い" /></ListItem>
                                <ListItem><ListItemIcon><ReportProblem color="warning" /></ListItemIcon><ListItemText primary="価値観やマナーの違い" /></ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', p: 1 }}>

                        </Box>
                    </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2, color: '#555' }}>
                    これらの悩みは、対策を知っていれば十分に乗り越えられます。本記事では、<b>ルームシェアで人間関係がうまくいかない時の対処法</b>を、実践的に解説します。
                </Typography>
            </Box>

            {/* 距離感の保ち方 */}
            <Box id="distance" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    上手な距離感の保ち方
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアにおける「距離感」は、快適な関係を築くポイントです。<b>近すぎず、遠すぎず</b>の絶妙なバランスが大切ですが、これは意識しなければ難しいもの。そこで、具体的なコツを紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f7fafd', borderRadius: 2 }}>
                            <List>
                                <ListItem>
                                    <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="自分の時間・空間を大切にする"
                                        secondary="「一人になりたい」と感じたときは遠慮せず、個室や外出でリフレッシュしましょう。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="適度な「挨拶」「声かけ」"
                                        secondary="毎日の何気ない挨拶や「お疲れさま」が、良好な関係の土台になります。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="無理に仲良くなろうとしない"
                                        secondary="深い付き合いを求めるより、自然体の距離感を意識しましょう。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary="プライベートな話題は慎重に"
                                        secondary="相手のプライバシーを尊重し、踏み込みすぎに注意しましょう。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <img
                                src="/images/blog/private-space-room_roomshare-human-relations-trouble-solution.jpg"
                                alt="ナチュラルなインテリアの個室でルームメイトの一人が読書し、自分の時間をリラックスして楽しんでいる様子。窓から自然光が入り、落ち着いた雰囲気を演出している。"
                                style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Chip icon={<CheckCircleOutline />} label="ポイント" color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2" display="inline">
                        「共有スペースはみんなで、個室や外出で1人の時間を」ルール化すると安心感が生まれます。
                    </Typography>
                </Box>
            </Box>

            {/* 話し合いのコツ */}
            <Box id="communication" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    話し合いのコツとコミュニケーション術
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    どんなに気をつけていても、時には「えっ」と感じる出来事や、ちょっとした不満が生まれるもの。そんな時こそ、<b>話し合い</b>が大事です。では、どう伝えればよいのでしょうか？
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2 }}>
                            <List>
                                <ListItem>
                                    <ListItemIcon><Chat color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="感情的にならず、冷静に伝える"
                                        secondary="「○○してくれない」ではなく「こうしてくれると嬉しいな」とポジティブな提案を意識。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="タイミングを選ぶ"
                                        secondary="お互いにリラックスしている時や、食事中など和やかな雰囲気で話すのが◎。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="伝えたいことを整理する"
                                        secondary="事前にポイントをメモしておくと、要点がブレません。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Chat color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="相手の意見も尊重する"
                                        secondary="聞く姿勢も大切。否定せず、まず受け止めてみましょう。"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                            <img
                                src="/images/blog/share-talk_roomshare-human-relations-trouble-solution.jpg"
                                alt="共有リビングのソファでルームメイト同士が和やかに話し合い、互いに意見を交換している様子。明るい光が差し込み、ゆったりした雰囲気が伝わってくる。"
                                style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Chip icon={<HelpOutline />} label="チェックポイント" color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="body2" display="inline">
                        「LINEやメモで伝える」のも一つの方法。直接だと緊張する時は、文章で気持ちを伝えてみましょう。
                    </Typography>
                </Box>
            </Box>

            {/* 専門家のアドバイス */}
            <Box id="advice" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家のアドバイスと実践例
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ここではルームシェア仲介やサポートを行う現場で実際に寄せられる相談や、解決に役立った事例をもとに、<b>「困った時の対処法」</b>をまとめます。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Lightbulb color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">事例1：家事分担のトラブル</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「掃除のやり方やタイミングが合わず、イライラする」との声。<b>チェック表やカレンダー</b>を使って「当番制」にしたことで、感情的なぶつかり合いが激減！
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Chip label="工夫ポイント" color="primary" size="small" sx={{ mr: 1 }} />
                                <Typography variant="body2" display="inline">
                                    「役割を明確に」「曖昧をなくす」ことが解決の鍵。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Lightbulb color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">事例2：生活リズムのすれ違い</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「夜勤と日勤で生活時間が合わない」場合、<b>音や照明のルール</b>を話し合って決めることで、お互いのストレスを軽減できたという例も。
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Chip label="工夫ポイント" color="primary" size="small" sx={{ mr: 1 }} />
                                <Typography variant="body2" display="inline">
                                    「小さなルールでも、事前に合意を」しておくのが安心。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            さらに！こんな工夫で「人間関係ストレス」を減らせる
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="月1回の「ルームメイト会議」" secondary="溜め込まずに、気になることを共有できる時間を作ってみましょう。" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="共通の目標やルールを明文化" secondary="例えば「夜10時以降は静かに」など、紙やグループチャットで共有。" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="「ありがとう」を言葉にする" secondary="日々の小さな感謝が、信頼関係を築く土台に。" />
                            </ListItem>
                        </List>
                    </Paper>
                </Box>
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/share-meeting_roomshare-human-relations-trouble-solution.jpg"
                        alt="ダイニングテーブルを囲み、ルームメイトたちが月一回のミーティングを開き、笑顔で意見交換やルール確認をしている様子。ナチュラルウッドの家具と観葉植物が温かみを加えている。"
                        style={{ width: '90%', maxWidth: 420, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    />
                </Box>
            </Box>

            {/* まとめとQ&A */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめとQ&A
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの人間関係は、<b>「他人と暮らす」</b>という特別な体験だからこそ、悩みが生まれやすいもの。でも、<b>距離感・話し合い・小さな工夫</b>を積み重ねることで、ストレスを減らし、より充実したシェア生活を楽しめます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        よくある質問Q&A
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><HelpOutline color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="Q. どうしても合わない場合はどうしたら？"
                                secondary="A. まずは一度、第三者（共通の友人など）に相談を。状況によっては、退去やルームメイト変更も選択肢です。無理せず、自分の心身を大切に。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HelpOutline color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="Q. ルールや分担はどこまで細かく決めるべき？"
                                secondary="A. 生活に関わる大切なポイント（掃除・ごみ出し・お金）は明確に。他は臨機応変でもOK。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HelpOutline color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="Q. どうしても不満が言いにくい…"
                                secondary="A. 直接言いづらい時は、メモやLINEで伝える・共通ノートを使うなど、間接的な方法も活用しましょう。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Chip label="ルームシェアの人間関係は「工夫」で必ず良くなる！" color="primary" variant="outlined" sx={{ fontSize: '1.1rem', p: 1.5 }} />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-human-relations-trouble-solution',
    slug: 'roomshare-human-relations-trouble-solution',
    title: 'ルームシェアで「人間関係がうまくいかない」時の対処法',
    excerpt: 'よくある人間関係の悩み 距離感の保ち方 話し合いのコツ 専門家アドバイス',
    coverImage: '/images/blog/cover-roomshare-human-relations_roomshare-human-relations-trouble-solution.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。カラフルなインテリアと穏やかな笑顔が調和し、理想的なシェア生活の雰囲気を伝えている。',
    publishedAt: '2024-06-16T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '人間関係', '暮らしの知恵'],
    tags: ['ルームシェア', '人間関係', 'コミュニケーション', '距離感', 'ストレス対策'],
    author: {
        name: '高橋 直人',
        avatar: '/images/avatars/takahashi-naoto.jpg',
        bio: 'ルームシェアや共同生活の相談・運営サポート歴10年以上。現場経験と心理学の知見を活かし、快適なシェアライフのヒントを発信中。',
        profilePicture: '/images/avatars/takahashi-naoto.jpg'
    },
    viewCount: 1078,
    contentComponent: PostContent
};

export default post;